export interface ParsedMarkdownTable {
  headers: string[];
  rows: string[][];
}

/**
 * Parse a markdown table string into structured data.
 * Handles GFM (GitHub Flavored Markdown) table format:
 *
 * | Header 1 | Header 2 |
 * |----------|----------|
 * | Cell 1   | Cell 2   |
 *
 * @param markdown - The markdown table string
 * @returns Parsed table with headers and rows arrays
 */
export function parseMarkdownTable(markdown: string): ParsedMarkdownTable {
  const lines = markdown.trim().split('\n');
  const headers: string[] = [];
  const rows: string[][] = [];

  let headerParsed = false;
  let separatorSkipped = false;

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) {
      continue;
    }

    // Must be a table row (starts and ends with |)
    if (!trimmedLine.startsWith('|') || !trimmedLine.endsWith('|')) {
      continue;
    }

    // Check if this is a separator line (contains only |, -, :, and spaces)
    if (/^\|[\s\-:|]+\|$/.test(trimmedLine)) {
      separatorSkipped = true;
      continue;
    }

    // Parse the cells: remove leading/trailing |, split by |, trim each cell
    const cells = trimmedLine
      .slice(1, -1) // Remove leading and trailing |
      .split('|')
      .map((cell) => cell.trim());

    if (!headerParsed) {
      // First non-separator row is the header
      headers.push(...cells);
      headerParsed = true;
    } else if (separatorSkipped) {
      // After header and separator, these are data rows
      rows.push(cells);
    }
  }

  return { headers, rows };
}
